/**
 * DateRangePicker Component
 *
 * A custom date range picker with predefined ranges.
 * Features:
 * - Preset date ranges (This Week, Last Week, etc.)
 * - Custom date range selection
 * - Dropdown interface
 */

import { useState, useRef, useEffect } from 'react';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subWeeks,
  subMonths,
  subYears,
  format
} from 'date-fns';

/**
 * Get predefined date ranges
 */
const getPresetRanges = () => {
  const today = new Date();

  return [
    {
      label: 'This Week',
      startDate: startOfWeek(today, { weekStartsOn: 0 }),
      endDate: endOfWeek(today, { weekStartsOn: 0 })
    },
    {
      label: 'Last Week',
      startDate: startOfWeek(subWeeks(today, 1), { weekStartsOn: 0 }),
      endDate: endOfWeek(subWeeks(today, 1), { weekStartsOn: 0 })
    },
    {
      label: 'This Month',
      startDate: startOfMonth(today),
      endDate: endOfMonth(today)
    },
    {
      label: 'Last Month',
      startDate: startOfMonth(subMonths(today, 1)),
      endDate: endOfMonth(subMonths(today, 1))
    },
    {
      label: 'This Year',
      startDate: startOfYear(today),
      endDate: endOfYear(today)
    },
    {
      label: 'Last Year',
      startDate: startOfYear(subYears(today, 1)),
      endDate: endOfYear(subYears(today, 1))
    }
  ];
};

/**
 * Format date to YYYY-MM-DD for input value
 */
const toInputFormat = (date) => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
};

/**
 * DateRangePicker Component
 */
function DateRangePicker({
  startDate,
  endDate,
  onChange,
  placeholder = 'Select date range'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [localStart, setLocalStart] = useState(startDate);
  const [localEnd, setLocalEnd] = useState(endDate);
  const containerRef = useRef(null);
  const presetRanges = getPresetRanges();

  // Sync local state with props
  useEffect(() => {
    setLocalStart(startDate);
    setLocalEnd(endDate);
  }, [startDate, endDate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetClick = (preset) => {
    setLocalStart(preset.startDate);
    setLocalEnd(preset.endDate);
    onChange({ startDate: preset.startDate, endDate: preset.endDate });
    setIsOpen(false);
  };

  const handleStartChange = (e) => {
    const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
    setLocalStart(date);
  };

  const handleEndChange = (e) => {
    const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
    setLocalEnd(date);
  };

  const handleApply = () => {
    onChange({ startDate: localStart, endDate: localEnd });
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setLocalStart(null);
    setLocalEnd(null);
    onChange({ startDate: null, endDate: null });
  };

  const formatDisplayDate = () => {
    if (!startDate && !endDate) {
      return placeholder;
    }

    const start = startDate ? format(startDate, 'MMM d, yyyy') : '';
    const end = endDate ? format(endDate, 'MMM d, yyyy') : '';

    if (start && end) {
      return `${start} - ${end}`;
    }
    return start || end;
  };

  const hasValue = startDate || endDate;

  return (
    <div className="relative" ref={containerRef}>
      <label className="text-xs font-medium text-gray-500 mb-1 block">Date Range</label>

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full min-w-[220px] border border-gray-300 rounded-md px-3 py-2 text-sm bg-white hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
      >
        <div className="flex items-center">
          <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className={hasValue ? 'text-gray-900' : 'text-gray-400'}>
            {formatDisplayDate()}
          </span>
        </div>

        <div className="flex items-center">
          {hasValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded mr-1"
            >
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 left-0 min-w-[380px]">
          <div className="flex">
            {/* Preset Ranges Sidebar */}
            <div className="border-r border-gray-200 py-2 w-32">
              {presetRanges.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Custom Date Range */}
            <div className="p-4 flex-1">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={toInputFormat(localStart)}
                    onChange={handleStartChange}
                    max={toInputFormat(localEnd) || undefined}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                  <input
                    type="date"
                    value={toInputFormat(localEnd)}
                    onChange={handleEndChange}
                    min={toInputFormat(localStart) || undefined}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleApply}
                  className="w-full bg-blue-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
